import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import { fetchPatientDetailsUuid, postPatientReport } from '../Redux/Thunk/Actions';
import '../styles/PatientReportFileTemplateCreate.scss';
import AnalysisTable from './AnalysisTable';
import FigmaHeader from './FigmaHeader/FigmaHeader';
import MedicalReportTemplate, {formatDateToDDMMYYYY} from './MedicalReportTemplate';
import { RenderSignature } from '../utils/templateUtils';
import embryologyLogo from '../assets/images/embryology-logo.png';

const isValidBranchAwareUhid = value => /^(ASCAS|VDASCAS)\d+$/i.test((value || '').trim());


function SemenFreezing(props) {
  const [show, setshow] = useState(false);
  const Content = useRef();
  const [valueChange, setvalueChange] = useState({
    // Patient Information
    patient: {
      gender1: 'Male',
      gender2: 'Female',
      patientId1: '',
      patientName1: '',
      age1: '',
      patientId2: '',
      patientName2: '',
      age2: ''
    },

    // Collection details
    sampleDetails: {
      dateOfCollection: '',
      timeOfCollection: '',
      timeOfAnalysis: '',
      abstinencePeriod: '',
      cryopreservationIndication: '',
      otherIndication: ''
    },

    // Semen Analysis - Pre-freezing
    macroscopic: {
      volume: '',
      appearance: '',
      viscosity: '',
      liquefactionTime: '',
      pH: '',
      vialsFrozen: '',
      semenValidity: '',
      gobletColour: '',
      canisterNo: '',
      tankNo: ''
    },

    microscopic: {
      spermConcentration: '',
      totalSpermCount: '',
      totalMotility: '',
      progressiveMotility: '',
      slowProgressiveMotility: '',
      nonProgressiveMotility: '',
      immotileSperm: '',
      fructose: '',
      vitality: '',
      totalMotileProgressive: '',
      agglutination: '',
      aggregation: '',
      roundCells: '',
      otherCells: ''
    },

    // Diagnosis
    comments: '',

    // Reporting
    reporting: {
      technician: '',
      technicianOtherName: '',
      androtechName: '',
      consultantName: '',
      clinicianSignature: ''
    }
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [patientOne_UUId, setPatientOne_UUId] = useState('');
  const [patientTwo_UUId, setPatientTwo_UUId] = useState('');

  const handleChange = e => {
    const text = e.target.value;

    if (e.target.name === 'patientId1' || e.target.name === 'patientId2') {
      if (isValidBranchAwareUhid(text)) {
        const fetchData = async () => {
          const payload = {p_uhid: text};
          props.loaderchange('true');
          const addPatient = fetchPatientDetailsUuid(payload);
          const response = await dispatch(addPatient);
          if (response.payload?.status?.code === 200) {
            const isSinglePatient = response.payload.data.data.length === 1;
            if (isSinglePatient) {
              const data = response.payload.data.data[0];
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;

              if (e.target.name === 'patientId1') {
                setPatientOne_UUId(data?.id);
                setvalueChange({
                  ...valueChange,
                  patient: {
                    ...valueChange.patient,
                    patientName1: namePat1,
                    age1: data?.age,
                    gender1: data?.gender,
                    patientId1: text
                  }
                });
              } else {
                setPatientTwo_UUId(data?.id);
                setvalueChange({
                  ...valueChange,
                  patient: {
                    ...valueChange.patient,
                    patientName2: namePat1,
                    age2: data?.age,
                    gender2: data?.gender,
                    patientId2: text
                  }
                });
              }
            } else {
              const data = response.payload.data.data[0].gender === 'Male' ? response.payload.data.data[0] : response.payload.data.data[1];
              const data2 = response.payload.data.data[1].gender === 'Female' ? response.payload.data.data[1] : response.payload.data.data[0];
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;
              const namePat2 = data2?.titles + ' ' + data2?.first_name + ' ' + data2?.last_name;

              setPatientOne_UUId(data?.id);
              setPatientTwo_UUId(data2?.id);
              setvalueChange({
                ...valueChange,
                patient: {
                  ...valueChange.patient,
                  patientName1: namePat1,
                  patientName2: namePat2,
                  age1: data?.age,
                  age2: data2?.age,
                  gender1: data?.gender,
                  gender2: data2?.gender,
                  patientId1: data?.p_uhid,
                  patientId2: data2?.p_uhid
                }
              });
            }
            props.loaderchange('false');
          } else {
            props.loaderchange('false');
            props.popupalert('true');
            props.popuptext('Network went wrong, Please try after some time.');
            setTimeout(() => {
              props.popupalert('false');
            }, 2000);
          }
        };
        fetchData();
      } else {
        setvalueChange({
          ...valueChange,
          patient: {
            ...valueChange.patient,
            [e.target.name]: text
          }
        });
      }
    } else {
      // Determine which group this field belongs to
      let groupName = '';
      if (e.target.name.startsWith('patientId') || e.target.name.startsWith('patientName') || e.target.name.startsWith('age') || e.target.name.startsWith('gender')) {
        groupName = 'patient';
      } else if (
        e.target.name.includes('Collection') ||
        e.target.name.includes('Examination') ||
        e.target.name.includes('Analysis') ||
        e.target.name === 'abstinencePeriod' ||
        e.target.name === 'cryopreservationIndication' ||
        e.target.name === 'otherIndication'
      ) {
        groupName = 'sampleDetails';
      } else if (
        e.target.name === 'volume' ||
        e.target.name === 'appearance' ||
        e.target.name === 'viscosity' ||
        e.target.name === 'pH' ||
        e.target.name === 'liquefactionTime' ||
        e.target.name === 'vialsFrozen' ||
        e.target.name === 'semenValidity' ||
        e.target.name === 'gobletColour' ||
        e.target.name === 'canisterNo' ||
        e.target.name === 'tankNo'
      ) {
        groupName = 'macroscopic';
      } else if (
        e.target.name === 'spermConcentration' ||
        e.target.name === 'totalSpermCount' ||
        e.target.name === 'progressiveMotility' ||
        e.target.name === 'slowProgressiveMotility' ||
        e.target.name === 'nonProgressiveMotility' ||
        e.target.name === 'totalMotility' ||
        e.target.name === 'immotileSperm' ||
        e.target.name === 'fructose' ||
        e.target.name === 'vitality' ||
        e.target.name === 'totalMotileProgressive' ||
        e.target.name === 'agglutination' ||
        e.target.name === 'aggregation' ||
        e.target.name === 'roundCells' ||
        e.target.name === 'otherCells'
      ) {
        groupName = 'microscopic';
      } else if (e.target.name === 'comments') {
        setvalueChange({...valueChange, comments: text});
        return;
      } else if (e.target.name === 'technician' || e.target.name === 'technicianOtherName' || e.target.name === 'androtechName' || e.target.name === 'consultantName' || e.target.name === 'clinicianSignature') {
        groupName = 'reporting';
      }

      if (groupName) {
        setvalueChange({
          ...valueChange,
          [groupName]: {
            ...valueChange[groupName],
            [e.target.name]: text
          }
        });
      }
    }
  };

  const handletest = async () => {
    try {
      if (
        patientOne_UUId !== '' &&
        valueChange.patient.patientId1 !== '' &&
        isValidBranchAwareUhid(valueChange.patient.patientId1) &&
        (valueChange.patient.patientId2 !== '' ? valueChange.patient.patientId1 !== valueChange.patient.patientId2 : true)
      ) {
        setshow(true);
        setTimeout(async () => {
          const htmlPayload = document.getElementById('reportcreate').innerHTML;
          const payload = {
            report_template: htmlPayload,
            patient_id: patientOne_UUId,
            description: '',
            patient_two: patientTwo_UUId,
            couple_id: valueChange.coupleId,
            template_type: 'Sperm Report'
          };

          props.loaderchange(true);
          const reportResponse = await dispatch(postPatientReport({type: '2', payload: payload}));

          if (reportResponse?.payload?.status?.code === 200) {
            props.loaderchange(false);
            navigate('/patientreports');
            Swal.fire({
              title: 'Successful.',
              text: 'Report added successful.',
              icon: 'success',
              showConfirmButton: false,
              timer: 1500
            });
          } else {
            setshow(false);
            props.loaderchange(false);
            props.popupalert(true);
            props.popuptext('Network went wrong, Please try after some time.');
            setTimeout(() => {
              props.popupalert(false);
            }, 2000);
          }
        }, 3000);
      } else {
        Swal.fire({
          title: 'Error.',
          text: 'Please enter valid patient id.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1500
        });
      }
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };

  const sideactive = useSelector(state => state.counter.sidebarnav);

  // MUI TimePicker handler - same as semen analysis
  const handleMuiTimeChange = (e, name) => {
    let hours = e.$H,
      mins = e.$m,
      ampm = 'AM';
    let temMin = e.$m;
    temMin < 10 ? (mins = '0' + temMin) : (mins = temMin);
    if (e.$H >= 12) {
      if (e.$H === 12) {
        hours = 12;
        ampm = 'PM';
      } else {
        let temHour = e.$H - 12;
        temHour < 10 ? (hours = '0' + temHour) : (hours = temHour);
        ampm = 'PM';
      }
    } else if (e.$H === 0) {
      hours = 12;
      ampm = 'AM';
    } else {
      let temHour = e.$H;
      temHour < 10 ? (hours = '0' + temHour) : (hours = temHour);
      ampm = 'AM';
    }

    // Store in sampleDetails group
    setvalueChange({...valueChange, sampleDetails: {...valueChange.sampleDetails, [name]: `${hours}-${mins}-${ampm}`}});
  };

  // MUI TimePicker render function - same as semen analysis
  const renderMuiTime = name => {
    return (
      <div className={!show ? 'show' : 'hide'}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoItem>
            <TimePicker onChange={e => handleMuiTimeChange(e, name)} />
          </DemoItem>
        </LocalizationProvider>
      </div>
    );
  };

  // Common styles
  const commonStyles = {
    input: {
      border: '1px solid #b0a4a4ff',
      outline: 'none',
      padding: '10px',
      backgroundColor: 'white',
      width: '200px'
    },
    label: {
      fontWeight: 600,
      whiteSpace: 'nowrap',
      fontSize: '14px'
    },
    separator: {
      fontWeight: 600,
      fontSize: '18px'
    }
  };

  // Calculate semen totals automatically
  useEffect(() => {
    const volume = parseFloat(valueChange.macroscopic.volume) || 0;
    const progressiveMotility = parseFloat(valueChange.microscopic.progressiveMotility) || 0;
    const slowProgressiveMotility = parseFloat(valueChange.microscopic.slowProgressiveMotility) || 0;
    const nonProgressiveMotility = parseFloat(valueChange.microscopic.nonProgressiveMotility) || 0;
    const spermConcentration = parseFloat(valueChange.microscopic.spermConcentration) || 0;

    const totalMotility = progressiveMotility + slowProgressiveMotility + nonProgressiveMotility;
    const totalSpermCount = volume * spermConcentration;
    const tmsc = volume * spermConcentration * (totalMotility / 100);
    const nextValues = {
      totalSpermCount: totalSpermCount > 0 ? totalSpermCount.toFixed(2) : '',
      totalMotility: totalMotility > 0 ? totalMotility.toFixed(2) : '',
      totalMotileProgressive: tmsc > 0 ? tmsc.toFixed(2) : ''
    };

    if (
      nextValues.totalSpermCount !== valueChange.microscopic.totalSpermCount ||
      nextValues.totalMotility !== valueChange.microscopic.totalMotility ||
      nextValues.totalMotileProgressive !== valueChange.microscopic.totalMotileProgressive
    ) {
      setvalueChange(prev => ({
        ...prev,
        microscopic: {
          ...prev.microscopic,
          ...nextValues
        }
      }));
    }
  }, [valueChange.macroscopic.volume, valueChange.microscopic.progressiveMotility, valueChange.microscopic.slowProgressiveMotility, valueChange.microscopic.nonProgressiveMotility, valueChange.microscopic.spermConcentration, valueChange.microscopic.totalSpermCount, valueChange.microscopic.totalMotility, valueChange.microscopic.totalMotileProgressive]);

  const REFERENCE_VALUES = [
    [
      {value: 'Volume', bold: true},
      {value: ': >=1.5 ml', bold: false},
      {value: 'Appearance and Colour', bold: true},
      {value: ': Whitish/ Greyish Opalescent', bold: false},
      {value: 'Viscosity', bold: true},
      {value: ': No Reference', bold: false}
    ],
    [
      {value: 'pH', bold: true},
      {value: ': 7.2-7.8', bold: false},
      {value: 'Time of Liquefaction', bold: true},
      {value: ': <60 Mins', bold: false},
      {value: 'Sperm Concentration', bold: true},
      {value: ': >=16 millions/ml ', bold: false}
    ],
    [
      {value: 'Total Sperm Number (Per Ejaculate)', bold: true},
      {value: ': >=39 million', bold: false},
      {value: 'Vitality', bold: true},
      {value: ': >54 %', bold: false},
      {value: 'Normal Form', bold: true},
      {value: ': >4%', bold: false}
    ],
    [
      {value: 'Motility', bold: true},
      {value: ': >=30% RPR+SPR (a+b)\n: >=42% PR+NP(a+b+c)', bold: false},
    ]
  ];

  const MACROSCOPIC_ROWS = [
    {
      label: 'Volume',
      name: 'volume',
      type: 'text',
      unit: '(ml)',
      width: '150px',
      reference: '>=1.5 ml'
    },
    {
      label: 'Appearance and Colour',
      name: 'appearance',
      type: 'text',
      width: '150px',
      reference: 'Whitish/ Greyish Opalescent'
    },
    {
      label: 'Viscosity',
      name: 'viscosity',
      type: 'text',
      width: '150px',
      reference: 'No Reference'
    },
    {
      label: 'pH',
      name: 'pH',
      type: 'text',
      width: '150px',
      reference: '7.2-7.8'
    },
    {
      label: 'Time of Liquefaction',
      name: 'liquefactionTime',
      type: 'number',
      unit: '(Mins)',
      width: '150px',
      reference: '<60 Mins'
    },
    {
      label: 'No. of Vials Frozen',
      name: 'vialsFrozen',
      type: 'text',
      unit: 'Count',
      width: '150px'
    },
    {
      label: 'Semen Validity',
      name: 'semenValidity',
      type: 'text',
      unit: 'Years',
      width: '150px'
    },
   
  ];

  const MICROSCOPIC_ROWS = [
    {
      label: 'Sperm Concentration',
      name: 'spermConcentration',
      type: 'text',
      unit: '(millions/ml)',
      width: '150px',
      reference: '>=16 millions/ml '
    },
    {
      label: 'Total Sperm Number (per ejaculate)',
      name: 'totalSpermCount',
      type: 'text',
      unit: '(million)',
      width: '150px',
      reference: '>=39 million'
    },
    {
      label: 'Rapid Progressive Motility(a)',
      name: 'progressiveMotility',
      type: 'text',
      unit: '%',
      width: '150px',
      reference: '>=30% RPR+SPR(A+B)'
    },
    {
      label: 'Slow Progressive Motility(b)',
      name: 'slowProgressiveMotility',
      type: 'text',
      unit: '%',
      width: '150px',
      reference: '<=42% PR+NP(A+B+C)'
    },
    {
      label: 'Non-Progressive Motility(c)',
      name: 'nonProgressiveMotility',
      type: 'text',
      unit: '%',
      width: '150px',
      reference: ''
    },
    {
      label: 'Total Motility',
      name: 'totalMotility',
      type: 'text',
      unit: '%',
      width: '150px',
      reference: ''
    },
    {
      label: 'Immotile Sperm',
      name: 'immotileSperm',
      type: 'text',
      unit: '%',
      width: '150px',
      reference: ''
    },
    {
      label: 'Fructose',
      name: 'fructose',
      type: 'text',
      width: '150px',
      reference: ''
    },
    {
      label: 'Total Motile Progressive(TMSC)',
      name: 'totalMotileProgressive',
      type: 'text',
      unit: 'volume*(a+b+c)*sperm concentration in million(formula)',
      width: '150px',
      reference: ''
    },
    {
      label: 'Vitality',
      name: 'vitality',
      type: 'text',
      unit: '%',
      width: '150px',
      reference: '>54%'
    },
    {
      label: 'Agglutination',
      name: 'agglutination',
      type: 'text',
      width: '150px',
      reference: ''
    },
    {
      label: 'Aggregation',
      name: 'aggregation',
      type: 'text',
      width: '150px',
      reference: ''
    },
    {
      label: 'Round Cells / HPF',
      name: 'roundCells',
      type: 'text',
      unit: 'cells/HPF',
      width: '150px',
      reference: ''
    },
    {
      label: 'Other Cells / Debris',
      name: 'otherCells',
      type: 'text',
      width: '150px',
      reference: ''
    }
  ];


  return (
    <>
      <div className={sideactive ? 'maincontainer-patient-sa' : 'maincontainer-patient'}>
        <div className='row'>
          <div className='col'>
            <h3 className='page-title'>Report Template</h3>
            <ul className='breadcrumb'>
              <li className='breadcrumb-item'>
                <a href='/' style={{textDecoration: 'none', color: 'black'}}>
                  Dashboard /
                </a>
                <a href='/patientreports' style={{textDecoration: 'none', color: 'black'}}>
                  &nbsp;Patient Reports
                </a>
              </li>
              <li className='breadcrumb-item active'>Report Template</li>
            </ul>
          </div>
        </div>
        {/* <h1>SEMEN FREEZING</h1> */}
        <div style={{backgroundColor: 'white', maxWidth: '100%'}} className='patientreport' id='reportcreate'>
          {show ? (
            <MedicalReportTemplate
              title='SEMEN FREEZING'
              clinicLogo={embryologyLogo}
              showDateTopRight={true}
              patientData={{
                patientOneId: valueChange.patient.patientId1,
                patientTwoId: valueChange.patient.patientId2,
                patientOneName: valueChange.patient.patientName1,
                patientTwoName: valueChange.patient.patientName2,
                genderOne: valueChange.patient.gender1,
                genderTwo: valueChange.patient.gender2,
                patientOneAge: valueChange.patient.age1,
                patientTwoAge: valueChange.patient.age2,
                date: new Date().toLocaleDateString('en-IN'),
                doctor: valueChange.doctor_name,
                modeOfCollection: 'Ejaculate',
                timeOfExamination: valueChange.sampleDetails.timeOfAnalysis || '',
                completeCollection: 'Yes',
                sampleCollection: valueChange.sampleDetails.cryopreservationIndication || '',
                timeOfCollection: valueChange.sampleDetails.timeOfCollection || '',
                sampleCollectionDate: valueChange.sampleDetails.dateOfCollection || new Date().toLocaleDateString('en-IN'),
                abstinencePeriod: valueChange.sampleDetails.abstinencePeriod ? `${valueChange.sampleDetails.abstinencePeriod} Days` : '',
                technician: valueChange.reporting.technician,
                technicianOtherName: valueChange.reporting.technicianOtherName,
                androtechName: valueChange.reporting.androtechName,
                consultantName: valueChange.reporting.consultantName,
                clinicianSignature: valueChange.reporting.clinicianSignature,
                androtechSignature: ''
              }}
              sections={[
                {
                  title: 'Collection Details',
                  layout: 'custom-grid',
                  data: [
                    // Left column - first 3 items
                    [
                      {label: 'Date of Collection', value: formatDateToDDMMYYYY(valueChange.sampleDetails.dateOfCollection) || ''},
                      {label: 'Time of Collection', value: valueChange.sampleDetails.timeOfCollection ? valueChange.sampleDetails.timeOfCollection.replace(/-/g, ':') : ''},
                      {label: 'Time of Analysis', value: valueChange.sampleDetails.timeOfAnalysis ? valueChange.sampleDetails.timeOfAnalysis.replace(/-/g, ':') : ''}
                    ],
                    // Right column - remaining 3 items
                    [
                      {label: 'Abstinence Period', value: valueChange.sampleDetails.abstinencePeriod ? `${valueChange.sampleDetails.abstinencePeriod} Days` : ''},
                      {label: 'Indication for Cryopreservation', value: valueChange.sampleDetails.cryopreservationIndication === 'Other' ? `${valueChange.sampleDetails.cryopreservationIndication}: ${valueChange.sampleDetails.otherIndication}` : valueChange.sampleDetails.cryopreservationIndication || ''}
                    ]
                  ]
                },
                {
                  title: 'Macroscopic Examination and Storage Details',
                  layout: 'custom-grid',
                  data: [
                    [
                      {label: 'Volume', value: valueChange.macroscopic.volume ? `${valueChange.macroscopic.volume} (ml)` : ''},
                      {label: 'Appearance and Colour', value: valueChange.macroscopic.appearance || ''},
                      {label: 'Viscosity', value: valueChange.macroscopic.viscosity || ''},
                      {label: 'pH', value: valueChange.macroscopic.pH || ''},
                    ],
                    [
                      {label: 'Time of Liquefaction', value: valueChange.macroscopic.liquefactionTime ? `${valueChange.macroscopic.liquefactionTime} (Mins)` : ''},
                      {label: 'No. of Vials Frozen', value: valueChange.macroscopic.vialsFrozen ? `${valueChange.macroscopic.vialsFrozen} Count` : ''},
                      {label: 'Semen Validity', value: valueChange.macroscopic.semenValidity ? `${valueChange.macroscopic.semenValidity} Years` : ''},
                      
                    ]
                  ]
                },
                {
                  title: 'Microscopic Examination',
                  layout: 'custom-grid',
                  data: [
                    [
                      {label: 'Sperm Concentration', value: valueChange.microscopic.spermConcentration ? `${valueChange.microscopic.spermConcentration} (millions/ml)` : ''},
                      {label: 'Total Sperm Number', value: valueChange.microscopic.totalSpermCount ? `${valueChange.microscopic.totalSpermCount} (million)` : ''},
                      {label: 'Rapid Progressive Motility(a)', value: valueChange.microscopic.progressiveMotility ? `${valueChange.microscopic.progressiveMotility} %` : ''},
                      {label: 'Slow Progressive Motility(b)', value: valueChange.microscopic.slowProgressiveMotility ? `${valueChange.microscopic.slowProgressiveMotility} %` : ''},
                      {label: 'Non-Progressive Motility(c)', value: valueChange.microscopic.nonProgressiveMotility ? `${valueChange.microscopic.nonProgressiveMotility} %` : ''},
                      {label: 'Total Motility', value: valueChange.microscopic.totalMotility ? `${valueChange.microscopic.totalMotility} %` : ''},
                      {label: 'Immotile Sperm', value: valueChange.microscopic.immotileSperm ? `${valueChange.microscopic.immotileSperm} %` : ''},
                      {label: 'Fructose', value: valueChange.microscopic.fructose || ''}
                    ],
                    [
                      {label: 'Total Motile Progressive(TMSC)', value: valueChange.microscopic.totalMotileProgressive || ''},
                      {label: 'Vitality', value: valueChange.microscopic.vitality ? `${valueChange.microscopic.vitality} %` : ''},
                      {label: 'Agglutination', value: valueChange.microscopic.agglutination || ''},
                      {label: 'Aggregation', value: valueChange.microscopic.aggregation || ''},
                      {label: 'Round Cells / HPF', value: valueChange.microscopic.roundCells ? `${valueChange.microscopic.roundCells} cells/HPF` : ''},
                      {label: 'Other Cells / Debris', value: valueChange.microscopic.otherCells || ''}
                    ]
                  ]
                }
              ]}
              impression=''
              advice=''
              comments={valueChange.comments}
              referenceValues={REFERENCE_VALUES}
              footerText={`Lab Reference: SF-2026-${new Date().toISOString().slice(0, 10).replace(/-/g, '')} | WHO Reference Values 2021 6th Edition`}
            />
          ) : (
            <>
              {/* Patient Information Header */}
              <FigmaHeader />

              {/* Patient & Couple Details */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: '3px',
                  border: '1px solid #000',
                  marginBottom: '20px'
                }}
                className='report-template-wrap1'>
                <div style={{width: '100%', display: 'block'}} id='patientInfo1'>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      gap: '3px',
                      backgroundColor: '#f7f7f7 ',
                      borderRadius: '6px',
                      padding: '16px 8px 16px 8px',
                      gap: '3px'
                    }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `125px 16px 250px`,
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                      <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Patient ID</span>
                      <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                      <input
                        type='text'
                        name='patientId1'
                        value={valueChange.patient.patientId1}
                        onChange={handleChange}
                        style={{
                          border: '1px solid #b0a4a4ff',
                          outline: 'none',
                          padding: '10px',
                          backgroundColor: 'white',
                          width: '250px'
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `125px 16px 250px`,
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                      <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Male Partner Name</span>
                      <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                      <input
                        type='text'
                        name='patientName1'
                        value={valueChange.patient.patientName1}
                        onChange={handleChange}
                        style={{
                          border: '1px solid #b0a4a4ff',
                          outline: 'none',
                          padding: '10px',
                          backgroundColor: 'white',
                          width: '250px'
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `125px 16px 250px`,
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                      <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Gender</span>
                      <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                      <select
                        name='gender1'
                        value={valueChange.patient.gender1}
                        onChange={handleChange}
                        style={{
                          border: '1px solid #b0a4a4ff',
                          outline: 'none',
                          padding: '10px',
                          backgroundColor: 'white',
                          width: '250px'
                        }}>
                        <option value='Male'>Male</option>
                        <option value='Female'>Female</option>
                        <option value='Others'>Others</option>
                        <option value='Decline to State'>Decline to State</option>
                      </select>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `125px 16px 250px`,
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                      <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Age</span>
                      <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                      <input
                        type='text'
                        name='age1'
                        value={valueChange.patient.age1}
                        onChange={handleChange}
                        style={{
                          border: '1px solid #b0a4a4ff',
                          outline: 'none',
                          padding: '10px',
                          backgroundColor: 'white',
                          width: '250px'
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className='patientreportcreate2' style={{width: '100%', height: '100%', backgroundColor: '#f7f7f7'}} id='patientInfo2'>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      gap: '3px',
                      backgroundColor: '#f7f7f7 ',
                      borderRadius: '6px',
                      padding: '16px 8px 16px 8px',
                      gap: '3px'
                    }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `125px 16px 250px`,
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                      <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Patient ID</span>
                      <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                      <input
                        type='text'
                        name='patientId2'
                        value={valueChange.patient.patientId2}
                        onChange={handleChange}
                        style={{
                          border: '1px solid #b0a4a4ff',
                          outline: 'none',
                          padding: '10px',
                          backgroundColor: 'white',
                          width: '250px'
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `125px 16px 250px`,
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                      <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Female Partner Name</span>
                      <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                      <input
                        type='text'
                        name='patientName2'
                        value={valueChange.patient.patientName2}
                        onChange={handleChange}
                        style={{
                          border: '1px solid #b0a4a4ff',
                          outline: 'none',
                          padding: '10px',
                          backgroundColor: 'white',
                          width: '250px'
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `125px 16px 250px`,
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                      <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Gender</span>
                      <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                      <select
                        name='gender2'
                        value={valueChange.patient.gender2}
                        onChange={handleChange}
                        style={{
                          border: '1px solid #b0a4a4ff',
                          outline: 'none',
                          padding: '10px',
                          backgroundColor: 'white',
                          width: '250px'
                        }}>
                        <option value='Male'>Male</option>
                        <option value='Female'>Female</option>
                        <option value='Others'>Others</option>
                        <option value='Decline to State'>Decline to State</option>
                      </select>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `125px 16px 250px`,
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                      <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Age</span>
                      <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                      <input
                        type='text'
                        name='age2'
                        value={valueChange.patient.age2}
                        onChange={handleChange}
                        style={{
                          border: '1px solid #b0a4a4ff',
                          outline: 'none',
                          padding: '10px',
                          backgroundColor: 'white',
                          width: '250px'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <h2 style={{textAlign: 'center', fontWeight: '600', backgroundColor: '#f7f7f7', padding: '10px'}}>SEMEN FREEZING </h2>

              {/* Collection Details */}
              <div style={{marginBottom: '20px'}}>
                <h2
                  style={{
                    textAlign: 'center',
                    backgroundColor: '#f7f7f7',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    fontSize: '18px',
                    fontWeight: '600',
                    padding: '5px 0 5px 8px',
                    margin: '8px 0'
                  }}>
                  🔬 Collection Details
                </h2>
                <div style={{padding: '20px'}}>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px'}}>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `160px 16px 200px`,
                          alignItems: 'center',
                          gap: '16px'
                        }}>
                        <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Date of Collection:</span>
                        <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                        <input
                          type='date'
                          name='dateOfCollection'
                          value={valueChange.sampleDetails.dateOfCollection}
                          onChange={handleChange}
                          style={{
                            border: '1px solid #b0a4a4ff',
                            outline: 'none',
                            padding: '10px',
                            backgroundColor: 'white',
                            width: '200px'
                          }}
                        />
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `160px 16px 200px`,
                          alignItems: 'center',
                          gap: '16px'
                        }}>
                        <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Time of Collection:</span>
                        <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                        {renderMuiTime('timeOfCollection')}
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `160px 16px 200px`,
                          alignItems: 'center',
                          gap: '16px'
                        }}>
                        <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Time of Analysis:</span>
                        <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                        {renderMuiTime('timeOfAnalysis')}
                      </div>
                    </div>

                    <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `200px 16px 200px auto`,
                          alignItems: 'center',
                          gap: '16px'
                        }}>
                        <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Abstinence Period:</span>
                        <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                        <input
                          type='text'
                          name='abstinencePeriod'
                          value={valueChange.sampleDetails.abstinencePeriod}
                          onChange={handleChange}
                          style={{
                            border: '1px solid #b0a4a4ff',
                            outline: 'none',
                            padding: '10px',
                            backgroundColor: 'white',
                            width: '200px'
                          }}
                        />
                        <span style={{fontSize: '14px'}}>days</span>
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `200px 16px 200px`,
                          alignItems: 'center',
                          gap: '16px'
                        }}>
                        <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Indication for Cryopreservation:</span>
                        <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                        <select
                          name='cryopreservationIndication'
                          value={valueChange.sampleDetails.cryopreservationIndication}
                          onChange={handleChange}
                          style={{
                            border: '1px solid #b0a4a4ff',
                            outline: 'none',
                            padding: '10px',
                            backgroundColor: 'white',
                            width: '200px'
                          }}>
                          <option value=''>Select</option>
                          <option value='Cancer'>Cancer</option>
                          <option value='Chemotherapy'>Chemotherapy</option>
                          <option value='Radiotherapy'>Radiotherapy</option>
                          <option value='Surgery'>Surgery</option>
                          <option value='Other'>Other</option>
                        </select>
                      </div>
                      {valueChange.sampleDetails.cryopreservationIndication === 'Other' && (
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: `200px 16px 200px`,
                            alignItems: 'center',
                            gap: '16px'
                          }}>
                          <span style={{fontWeight: 600, whiteSpace: 'nowrap', fontSize: '14px'}}>Other Indication:</span>
                          <span style={{fontWeight: 600, fontSize: '18px'}}>:</span>
                          <input
                            type='text'
                            name='otherIndication'
                            value={valueChange.sampleDetails.otherIndication}
                            onChange={handleChange}
                            style={{
                              border: '1px solid #b0a4a4ff',
                              outline: 'none',
                              padding: '10px',
                              backgroundColor: 'white',
                              width: '200px'
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Macroscopic Section */}
                <div style={{marginBottom: '20px'}}>
                  <h2
                    style={{
                      textAlign: 'center',
                      backgroundColor: '#f7f7f7',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      fontSize: '18px',
                      fontWeight: '600',
                      padding: '5px 0 5px 8px',
                      margin: '8px 0'
                    }}>
                    ⚗️ Macroscopic Examination and Storage Details
                  </h2>
                  <AnalysisTable
                    headers={['Parameter', 'Result', 'WHO Reference Values 2021 6th Edition ']}
                    rows={MACROSCOPIC_ROWS}
                    show={show}
                    onChange={handleChange}
                    values={valueChange}
                    commonStyles={commonStyles}
                  />
                </div>

                {/* Microscopic Section */}
                <div style={{marginBottom: '20px'}}>
                  <h2
                    style={{
                      textAlign: 'center',
                      backgroundColor: '#f7f7f7',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      fontSize: '18px',
                      fontWeight: '600',
                      padding: '5px 0 5px 8px',
                      margin: '8px 0'
                    }}>
                    🔬 Microscopic Examination
                  </h2>
                  <AnalysisTable
                    headers={['Parameter', 'Result', 'WHO Reference Values 2021 6th Edition ']}
                    rows={MICROSCOPIC_ROWS}
                    show={show}
                    onChange={handleChange}
                    values={valueChange}
                    commonStyles={commonStyles}
                  />
                </div>

             

                   {/* Comments */}
                <div style={{marginBottom: '10px'}}>
                  <h2
                    style={{
                      textAlign: 'center',
                      backgroundColor: '#f7f7f7',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      fontSize: '18px',
                      fontWeight: '600',
                      padding: '5px 0 5px 8px',
                      margin: '8px 0'
                    }}>
                    📋 Comments
                  </h2>
                  {!show ? (
                    <textarea
                      name='comments'
                      value={valueChange.comments}
                      onChange={handleChange}
                      style={{
                        border: '1px solid #b0a4a4ff',
                        outline: 'none',
                        padding: '10px',
                        backgroundColor: 'white',
                        width: '100%',
                        minHeight: '60px',
                        fontFamily: 'Arial, sans-serif',
                        marginTop: '5px'
                      }}
                      placeholder='Enter clinical comments and recommendations...'
                    />
                  ) : (
                    valueChange.comments && (
                      <div
                        style={{
                          border: '1px solid #ddd',
                          padding: '10px',
                          minHeight: '40px',
                          backgroundColor: '#f9f9f9',
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'Arial, sans-serif',
                          marginTop: '5px'
                        }}>
                        {valueChange.comments}
                      </div>
                    )
                  )}
                </div>

                {/* Standardized Signature Section */}
                <RenderSignature show={show} valueChange={valueChange} handleChange={handleChange} commonStyles={commonStyles} />

                {/* Footer padding/spacing */}
                <div style={{overflowX: 'hidden', maxWidth: '97%', margin: '0 auto'}}>
                  <div style={{padding: '16px 8px 16px 8px'}}>
                    <span style={{marginLeft: '65%', display: 'block', marginBottom: '16px', fontWeight: '600'}}>
                      {/* Clinician Signature and Technician Signature */}
                    </span>
                    <span style={{display: 'block', marginBottom: '16px', fontSize: '12px'}}>
                      <strong>*Percentage respect to total number of abnormal spermatoza</strong>
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className='Footer'>
          <button onClick={handletest} className='Button_Create'>
            Add Report
          </button>
        </div>
      </div>
    </>
  );
}

export default SemenFreezing;
